import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X, User as UserIcon, LogOut, LayoutDashboard, Bookmark, Rss, Globe, Check, PenLine } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PushBell } from "@/components/PushBell";
import { NotificationBell } from "@/components/NotificationBell";
import { StreakBadge } from "@/components/StreakBadge";
import { LayerNav } from "@/components/layernav/LayerNav";
import { MobileNav } from "@/components/layernav/MobileNav";
import { ReadingProgress } from "@/components/ReadingProgress";
import { useAuth } from "@/context/AuthContext";
import { useLang, LANGUAGES, articlePath } from "@/lib/i18n";
import { useSparkle } from "@/lib/sparkle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ADMIN_ROLES = ["superadmin", "editor", "analyst", "social_manager"];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const sparkle = useSparkle();
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const onScroll = () => setCompact((c) => (c ? window.scrollY > 60 : window.scrollY > 150));
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const changeLang = (code: string) => {
    setLang(code);
    const m = location.pathname.match(/^(?:\/([a-z]{2}))?\/article\/(.+)$/);
    if (m) navigate(articlePath(code, m[2]), { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className={`flex items-center justify-center overflow-hidden transition-all duration-300 ${
          compact ? "max-h-0 opacity-0" : "max-h-16 opacity-100 pt-2.5 pb-0.5 lg:pt-3 lg:pb-1"
        }`} data-testid="mobile-logo-row">
          <Logo />
        </div>
        <div className="flex items-center justify-between h-12">
          <div className="flex flex-1 items-center gap-1">
            <Button variant="ghost" size="icon" data-testid="nav-mobile-toggle"
              aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileOpen}
              className="lg:hidden text-gray-300 -ml-2" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {compact && (
              <div className="animate-in fade-in slide-in-from-left-2 duration-300" data-testid="compact-logo">
                <Logo compact />
              </div>
            )}
          </div>
          <LayerNav />
          <div className="flex flex-1 items-center justify-end gap-1 sm:gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="nav-language-button"
                  aria-label="Change language"
                  className="text-gray-400 hover:text-white">
                  <Globe className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end"
                className="bg-[#121212] border-white/10 text-white max-h-[70vh] overflow-y-auto">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`lang-option-${l.code}`}
                    onClick={() => changeLang(l.code)} className="justify-between gap-4">
                    {l.label}
                    {lang === l.code && <Check className="h-4 w-4 text-[#00E5FF]" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <StreakBadge />
            <PushBell />
            <NotificationBell />
            <Button variant="ghost" size="icon" data-testid="nav-search-button"
              aria-label="Search"
              className="text-gray-400 hover:text-white" onClick={() => navigate("/search")}>
              <Search className="h-4 w-4" />
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="nav-user-menu"
                    className="border-white/20 bg-transparent text-white hover:bg-white/5 rounded-md">
                    <UserIcon className="h-4 w-4 mr-1.5" />
                    <span className="hidden sm:inline max-w-[110px] truncate">{user.full_name?.split(" ")[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#121212] border-white/10 text-white">
                  <DropdownMenuItem data-testid="nav-account-link" onClick={() => navigate("/account")}>
                    <Bookmark className="h-4 w-4 mr-2" /> {t("myAccount")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-my-feed-link" onClick={() => navigate("/my-feed")}>
                    <Rss className="h-4 w-4 mr-2" /> {t("myFeed")}
                  </DropdownMenuItem>
                  {user.author_status === "approved" && (
                    <DropdownMenuItem data-testid="nav-author-studio-link" onClick={() => navigate("/studio")}>
                      <PenLine className="h-4 w-4 mr-2" /> Author Studio
                    </DropdownMenuItem>
                  )}
                  {ADMIN_ROLES.includes(user.role) && (
                    <DropdownMenuItem data-testid="nav-admin-link" onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="nav-logout-button"
                    onClick={async () => { await logout(); navigate("/"); }}>
                    <LogOut className="h-4 w-4 mr-2" /> {t("signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Button size="sm" variant="outline" data-testid="nav-login-link" onClick={() => navigate("/login")}
                  className="w-[84px] sm:w-[96px] border-[#7C3AED] bg-transparent text-white font-bold hover:bg-[#7C3AED]/10 hover:text-white rounded-md whitespace-nowrap">
                  {t("signin")}
                </Button>
                <Button size="sm" data-testid="nav-signup-button" onClick={() => navigate("/signup")}
                  className={`w-[84px] sm:w-[96px] bg-[#7C3AED] text-white font-bold hover:bg-[#6b2fd6] rounded-md whitespace-nowrap ${sparkle ? "sparkle-btn" : ""}`}>
                  {t("subscribe")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />
      <ReadingProgress />
    </header>
  );
};
